import { useState, useEffect, useMemo, useCallback } from "react";
import {
  Loader2, Sparkles, Calendar, Clock, Phone, Mail, User, MessageSquare,
  Check, X, Plus, ChevronDown, ChevronRight, Trash2, Edit3, ArrowRight,
  Link as LinkIcon, CheckCircle2, MoreHorizontal, Search, ArrowUpDown, Filter,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { AdminLayout } from "../components/admin/AdminSidebar";
import { EmailStatusBadge } from "../components/admin/EmailStatus";
import { TrialConversionModal } from "../components/admin/TrialConversionModal";
import { SmsReplyModal } from "../components/admin/SmsReplyModal";
import { CloseTrialModal, OutcomeSummary } from "../components/admin/CloseTrialModal";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getToken = () => localStorage.getItem("adminToken") || "";
const headers = () => ({ Authorization: `Bearer ${getToken()}` });

function formatTime(t) {
  if (!t) return "";
  const [h, m] = String(t).split(":");
  const hr = parseInt(h);
  const ampm = hr >= 12 ? "PM" : "AM";
  const hr12 = hr === 0 ? 12 : hr > 12 ? hr - 12 : hr;
  return `${hr12}:${m} ${ampm}`;
}

function formatDate(d) {
  if (!d) return "";
  try {
    return new Date(d + "T00:00:00").toLocaleDateString("en-AU",
      { day: "numeric", month: "short", year: "numeric" });
  } catch { return d; }
}

const STATUS_STYLES = {
  new:                "bg-blue-50 text-blue-600",
  contacted:          "bg-amber-50 text-amber-600",
  booked:             "bg-green-50 text-green-600",
  closed:             "bg-[#282828]/5 text-[#676664]/50",
  converted:          "bg-emerald-50 text-emerald-700",
  follow_up:          "bg-amber-100 text-amber-700",
  no_show:            "bg-gray-100 text-gray-500",
  customer_cancelled: "bg-red-50 text-red-500",
  admin_cancelled:    "bg-red-50 text-red-500",
  rescheduled:        "bg-indigo-50 text-indigo-600",
  did_not_convert:    "bg-gray-100 text-gray-600",
  could_not_contact:  "bg-gray-100 text-gray-500",
};

const APPT_STATUS_STYLES = {
  confirmed:          "bg-green-50 text-green-700",
  cancelled:          "bg-red-50 text-red-500",
  rescheduled:        "bg-indigo-50 text-indigo-600",
  completed:          "bg-emerald-50 text-emerald-700",
  no_show:            "bg-gray-100 text-gray-500",
  pending:            "bg-amber-50 text-amber-700",
};

// ── Filter/sort persistence ─────────────────────────────────────────────
const STORAGE_KEY = "admin_trial_leads_ui_v1";
const readUiState = () => {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}"); }
  catch { return {}; }
};
const writeUiState = (state) => {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(state)); } catch { /* ignore quota errors */ }
};

// Normalise an email for suggestion/candidate matching parity with backend.
const normEmail = (e) => String(e || "").trim().toLowerCase();

// Count linked rentals across all canonical + legacy fields, deduplicated.
// Section 6B Part 2 rule: a single BK-* rolls up into its parent group card
// ONLY when (a) that group id is EXPLICITLY in `linked_booking_group_ids`,
// OR (b) two or more of the lead's linked singles share the same
// `admin_booking_group_id` (Nidhi's case — 2 children of MBG-D8CDF92550 →
// one logical group rental). A single lone child of a group stays as a
// single (Jess's BK-C4B58D28EF child of MBG-108EF69485 stays "1 rental").
function _rollupGroupIds(lead, bookingsById) {
  const explicit = new Set((lead?.linked_booking_group_ids || []).filter(Boolean));
  // Dedupe: converted_booking_id often equals linked_booking_ids[0] — without
  // this the same BK-* was counted twice and triggered the ≥2 rollup rule
  // for a single-child link (Jess's phantom `linked-group-MBG-108EF69485` bug).
  const singles = Array.from(new Set([
    ...(lead?.linked_booking_ids || []),
    ...(lead?.converted_booking_id ? [lead.converted_booking_id] : []),
  ].filter(Boolean)));
  const groupCounts = {};
  singles.forEach(bid => {
    const gid = bookingsById?.[bid]?.admin_booking_group_id;
    if (gid) groupCounts[gid] = (groupCounts[gid] || 0) + 1;
  });
  Object.entries(groupCounts).forEach(([gid, n]) => { if (n >= 2) explicit.add(gid); });
  return explicit;
}

function linkedRentalCount(lead, bookingsById) {
  const singles = Array.isArray(lead?.linked_booking_ids) ? lead.linked_booking_ids : [];
  const legacy  = lead?.converted_booking_id ? [lead.converted_booking_id] : [];
  const rollupGroups = _rollupGroupIds(lead, bookingsById);
  const logical = new Set();
  rollupGroups.forEach(gid => logical.add(`g:${gid}`));
  [...singles, ...legacy].forEach(bid => {
    if (!bid) return;
    const b = bookingsById?.[bid];
    const gid = b?.admin_booking_group_id;
    if (gid && rollupGroups.has(gid)) logical.add(`g:${gid}`);
    else logical.add(`b:${bid}`);
  });
  return logical.size;
}

// Rev 2 — resolve the customer phone that should be shown for a lead.
// Priority: valid appointment phone > lead phone. Empty and env/business
// fallback numbers are ignored. We never fall through to any customer
// record or linked-rental phone from this page because that data isn't
// loaded here; when it isn't available on the appointment we surface the
// lead phone (which was captured directly from the enquiry form).
const _INVALID_PHONE_TOKENS = new Set([
  "", "0", "00", "000", "0000", "00000", "000000",
  "n/a", "na", "none", "null", "undefined",
]);
function _isUsablePhone(p) {
  if (!p) return false;
  const clean = String(p).trim().toLowerCase();
  if (_INVALID_PHONE_TOKENS.has(clean)) return false;
  if (!/\d/.test(clean)) return false;
  return true;
}
function resolveCustomerPhone(lead, appts) {
  const list = Array.isArray(appts) ? appts : (appts ? [appts] : []);
  for (const a of list) {
    if (_isUsablePhone(a?.customer_phone)) return a.customer_phone;
  }
  if (_isUsablePhone(lead?.phone)) return lead.phone;
  return "";
}

// Rev 2 — for a lead with multiple appointments, pick the "primary" one
// for the collapsed row / edit-modal target. Priority:
//   1. Appointment whose id matches the lead's converted_booking_id chain
//      OR whose lead_id is explicitly set to this lead.
//   2. Latest by date + time.
function primaryAppointmentFor(lead, appts) {
  const list = Array.isArray(appts) ? appts : (appts ? [appts] : []);
  if (!list.length) return null;
  const explicit = list.find(a => a.lead_id && a.lead_id === lead.id);
  if (explicit) return explicit;
  const sorted = [...list].sort((a, b) => {
    const ka = `${a.date || ""}T${a.time || "00:00"}`;
    const kb = `${b.date || ""}T${b.time || "00:00"}`;
    return kb.localeCompare(ka);
  });
  return sorted[0];
}

// Trial date/time source: linked appointment first (canonical), else lead
// preferences. Returns an ISO-like sortable key + display strings.
function trialDateFor(lead, appt) {
  const date = appt?.date || lead?.preferred_date || "";
  const time = appt?.time || lead?.preferred_time || "";
  return { date, time, sortKey: date ? `${date}T${time || "00:00"}` : "" };
}

// ────────────────────────────────────────────────────────────────────────
// Compact collapsed lead row + independent expand panel.
// ────────────────────────────────────────────────────────────────────────
function LeadRow({
  lead, appts, bookingsById, expanded, onToggle, onOpenLinkage, onOpenOutcome, onSendSms,
  onCreateBooking, onEditBooking, onDelete, onStatusChange, onEditNotes,
  onUnlinkBooking, onUnlinkGroup,
}) {
  const appt = primaryAppointmentFor(lead, appts);
  const otherAppts = (Array.isArray(appts) ? appts : []).filter(a => a && a.id !== appt?.id);
  const rentalCount = linkedRentalCount(lead, bookingsById);
  const { date, time } = trialDateFor(lead, appt);
  const outcomeKey = lead.outcome || lead.status || "new";
  const appointmentStatus = appt?.status || (lead.status === "booked" ? "confirmed" : "—");
  const resolvedPhone = resolveCustomerPhone(lead, appts);

  // Rev 2 — group linked bookings by admin_booking_group_id so grouped
  // rentals render as one bucket with their children nested underneath.
  // Section 6B Part 2 fix: a single BK-* only rolls up into a group card
  // when that group id is EXPLICITLY in `linked_booking_group_ids`. If the
  // admin linked only a specific child booking (as with Jess's
  // BK-C4B58D28EF), it must stay as a standalone `linked-booking-<bid>`
  // card and its Unlink action must target that single booking id — never
  // the whole parent group by accident.
  const linkedRentalsGrouped = useMemo(() => {
    const rollupGroupIds = _rollupGroupIds(lead, bookingsById);
    const singleIds = [
      ...(lead?.linked_booking_ids || []),
      ...((lead?.converted_booking_id && !(lead?.linked_booking_ids || []).includes(lead.converted_booking_id))
        ? [lead.converted_booking_id] : []),
    ].filter(Boolean);
    const groupBuckets = {};
    const bareSingles = [];
    singleIds.forEach(bid => {
      const b = bookingsById?.[bid];
      const gid = b?.admin_booking_group_id;
      if (gid && rollupGroupIds.has(gid)) {
        if (!groupBuckets[gid]) groupBuckets[gid] = { gid, children: [] };
        groupBuckets[gid].children.push({ id: bid, ...b });
      } else {
        bareSingles.push({ id: bid, ...(b || {}) });
      }
    });
    // For any group id that is explicitly linked (linked_booking_group_ids)
    // but had no matching single above, render its full sibling list.
    (lead?.linked_booking_group_ids || []).forEach(gid => {
      if (!groupBuckets[gid]) groupBuckets[gid] = { gid, children: [] };
      Object.values(bookingsById || {}).forEach(b => {
        if (b?.admin_booking_group_id === gid && !groupBuckets[gid].children.some(c => c.id === b.id)) {
          groupBuckets[gid].children.push(b);
        }
      });
    });
    // For Nidhi-style implicit rollups (two singles → one group card), also
    // enrich with siblings so the third child is visible.
    Object.keys(groupBuckets).forEach(gid => {
      Object.values(bookingsById || {}).forEach(b => {
        if (b?.admin_booking_group_id === gid && !groupBuckets[gid].children.some(c => c.id === b.id)) {
          groupBuckets[gid].children.push(b);
        }
      });
    });
    return { groups: Object.values(groupBuckets), singles: bareSingles };
  }, [lead, bookingsById]);

  // Primary action button (contextual).
  const primary = useMemo(() => {
    if (rentalCount > 0) {
      return {
        key: "view_linked",
        label: rentalCount === 1 ? "1 rental linked" : `${rentalCount} rentals linked`,
        onClick: () => onOpenLinkage(lead),
        testid: `linked-rentals-${lead.id}`,
        className: "text-[#9A7B4F] bg-[#9A7B4F]/8 border border-[#9A7B4F]/30 hover:bg-[#9A7B4F]/15",
      };
    }
    if (lead.status === "new") return {
      key: "mark_contacted",
      label: "Mark contacted", onClick: () => onStatusChange(lead.id, { status: "contacted" }),
      testid: `mark-contacted-${lead.id}`,
      className: "text-amber-700 border border-amber-200 hover:bg-amber-50",
    };
    if (lead.status === "contacted") return {
      key: "create_booking",
      label: "Create booking", onClick: () => onCreateBooking(lead),
      testid: `create-booking-${lead.id}`,
      className: "text-white bg-[#9A7B4F] hover:bg-[#8B6B3F]",
    };
    if (lead.status === "booked" && !lead.outcome) return {
      key: "mark_outcome",
      label: "Mark outcome", onClick: () => onOpenOutcome(lead),
      testid: `mark-outcome-${lead.id}`,
      className: "text-emerald-700 border border-emerald-200 hover:bg-emerald-50",
    };
    return {
      key: "link_rental",
      label: "Link rental", onClick: () => onOpenLinkage(lead),
      testid: `link-rental-${lead.id}`,
      className: "text-[#9A7B4F] border border-[#9A7B4F]/30 hover:bg-[#9A7B4F]/5",
    };
  }, [lead, rentalCount, onCreateBooking, onOpenLinkage, onOpenOutcome, onStatusChange]);

  return (
    <div className="bg-white border border-[#282828]/8" data-testid={`lead-${lead.id}`}>
      {/* Collapsed row (always visible) */}
      <button
        type="button"
        onClick={() => onToggle(lead.id)}
        className="w-full text-left px-4 py-3 hover:bg-[#FAF8F0]/40 flex items-center gap-3"
        data-testid={`lead-row-${lead.id}`}
        aria-expanded={expanded}
      >
        <span className="w-5 flex-shrink-0 text-[#676664]/40">
          {expanded ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
        </span>
        {/* Customer name */}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <User className="w-3.5 h-3.5 text-[#9A7B4F] flex-shrink-0" strokeWidth={1.5} />
            <span className="text-[13px] text-[#282828] font-medium truncate">{lead.name || "—"}</span>
          </div>
        </div>
        {/* Trial date + time */}
        <div className="hidden md:flex flex-col items-end text-[11px] text-[#676664] min-w-[140px]" data-testid={`lead-trial-when-${lead.id}`}>
          <span>{date ? formatDate(date) : <span className="text-[#676664]/40 italic">no trial date</span>}</span>
          {time && <span className="text-[10px] text-[#676664]/60">{formatTime(time)}</span>}
        </div>
        {/* Appointment status */}
        <span className={`hidden md:inline text-[9px] px-2 py-0.5 tracking-wider uppercase font-medium ${APPT_STATUS_STYLES[appointmentStatus] || "bg-[#282828]/5 text-[#676664]/60"}`}
              data-testid={`lead-appt-status-${lead.id}`}>
          {appointmentStatus}
        </span>
        {/* Section 18 — Attendance pill */}
        {(() => {
          const att = lead.attendance_status || "";
          if (!att || lead.status === "new" || lead.status === "contacted") return null;
          const cls = ({
            confirmed_attending: "bg-emerald-50 text-emerald-700 border border-emerald-200",
            not_attending: "bg-red-50 text-red-600 border border-red-200",
            no_response: "bg-slate-100 text-slate-600 border border-slate-200",
            cancelled: "bg-gray-100 text-gray-500 border border-gray-200",
            completed: "bg-blue-50 text-blue-600 border border-blue-200",
          })[att] || "bg-amber-50 text-amber-700 border border-amber-200";
          const lbl = ({
            awaiting_response: "Awaiting response",
            confirmed_attending: "Attending",
            not_attending: "Not attending",
            no_response: "No response",
            cancelled: "Cancelled",
            completed: "Completed",
          })[att] || att;
          return (
            <span className={`hidden md:inline text-[9px] px-2 py-0.5 tracking-wider uppercase font-medium ${cls}`}
                  data-testid={`lead-attendance-${lead.id}`}>
              {lbl}
            </span>
          );
        })()}
        {/* Trial outcome (or lead status) */}
        <span className={`text-[9px] px-2 py-0.5 tracking-wider uppercase font-medium ${STATUS_STYLES[outcomeKey] || STATUS_STYLES.new}`}
              data-testid={`lead-outcome-${lead.id}`}>
          {(lead.outcome || lead.status || "new").replace(/_/g, " ")}
        </span>
        {/* Linked rentals count */}
        <span className={`hidden sm:inline text-[10px] px-2 py-0.5 border ${rentalCount > 0 ? "border-[#9A7B4F]/40 text-[#9A7B4F] bg-[#9A7B4F]/8" : "border-[#282828]/10 text-[#676664]/50"}`}
              data-testid={`lead-linked-count-${lead.id}`}>
          {rentalCount === 0 ? "no rental" : rentalCount === 1 ? "1 rental" : `${rentalCount} rentals`}
        </span>
        {/* Primary action (stopPropagation so click doesn't toggle expand) */}
        <span
          role="button"
          tabIndex={0}
          onClick={(e) => { e.stopPropagation(); primary.onClick(); }}
          onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.stopPropagation(); primary.onClick(); } }}
          className={`text-[10px] px-3 py-1.5 tracking-wider uppercase cursor-pointer ${primary.className}`}
          data-testid={primary.testid}
        >
          {primary.label}
        </span>
      </button>

      {/* Expanded panel */}
      {expanded && (
        <div className="border-t border-[#282828]/8 bg-[#FAF8F0]/25 px-5 py-4" data-testid={`lead-expanded-${lead.id}`}>
          {/* Contact + notes */}
          <div className="grid md:grid-cols-2 gap-4 mb-4">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-[#676664]/50 mb-1">Contact</div>
              <div className="text-[12px] text-[#282828] space-y-0.5">
                <div className="flex items-center gap-1.5"><Mail className="w-3 h-3 text-[#676664]/50" /> {lead.email || "—"}</div>
                <div className="flex items-center gap-1.5" data-testid={`lead-phone-${lead.id}`}>
                  <Phone className="w-3 h-3 text-[#676664]/50" /> {resolvedPhone || "—"}
                </div>
                <div className="text-[10px] text-[#676664]/50">Received {new Date(lead.created_at).toLocaleDateString("en-AU",
                  { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}</div>
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-[#676664]/50 mb-1">Customer notes</div>
              <div className="text-[12px] text-[#676664] italic">
                {lead.notes ? lead.notes : <span className="text-[#676664]/40">No notes provided by customer.</span>}
              </div>
              {lead.admin_notes && (
                <div className="mt-2 text-[11px] text-amber-800 bg-amber-50 border border-amber-100 px-2 py-1.5">
                  Admin: {lead.admin_notes}
                </div>
              )}
              <button type="button" onClick={() => onEditNotes(lead)}
                      className="mt-1 text-[10px] text-[#9A7B4F] hover:underline"
                      data-testid={`edit-admin-notes-${lead.id}`}>
                {lead.admin_notes ? "Edit admin note" : "Add admin note"}
              </button>
            </div>
          </div>

          {/* Appointment details (primary + additional if the lead has more than one) */}
          <div className="mb-4">
            <div className="text-[10px] uppercase tracking-wider text-[#676664]/50 mb-1">
              Appointment{otherAppts.length ? `s (${otherAppts.length + 1})` : ""}
            </div>
            {appt ? (
              <div className="space-y-2">
                {[appt, ...otherAppts].map(a => (
                  <div key={a.id} className="bg-white border border-[#282828]/8 p-3 text-[12px] text-[#282828]"
                       data-testid={`appt-block-${a.id}`}>
                    <div className="flex flex-wrap items-center gap-3">
                      <span className="inline-flex items-center gap-1 text-[#282828]">
                        <Calendar className="w-3 h-3 text-[#676664]/60" /> {formatDate(a.date)}
                      </span>
                      <span className="inline-flex items-center gap-1 text-[#282828]">
                        <Clock className="w-3 h-3 text-[#676664]/60" /> {formatTime(a.time)} · {a.duration_minutes || 60}m
                      </span>
                      <span className={`text-[9px] px-2 py-0.5 tracking-wider uppercase ${APPT_STATUS_STYLES[a.status] || "bg-[#282828]/5"}`}>
                        {a.status}
                      </span>
                      <span className="text-[10px] text-[#676664]/50">{a.id}</span>
                      <div className="ml-auto flex items-center gap-1">
                        <button type="button" onClick={() => onEditBooking(a)}
                                className="p-1.5 text-[#676664]/60 hover:text-[#9A7B4F]" title="Edit booking"
                                data-testid={`edit-appointment-${a.id}`}>
                          <Edit3 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                    {a.notes && <div className="mt-2 text-[11px] text-[#676664]/70 italic">{a.notes}</div>}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-[12px] text-[#676664]/50 italic">No confirmed appointment.</div>
            )}
          </div>

          {/* Outcome history */}
          {lead.outcome && (
            <div className="mb-4">
              <div className="text-[10px] uppercase tracking-wider text-[#676664]/50 mb-1">Outcome</div>
              <OutcomeSummary appointment={lead} />
            </div>
          )}

          {/* Comms activity */}
          <div className="mb-4">
            <div className="text-[10px] uppercase tracking-wider text-[#676664]/50 mb-1">Email &amp; SMS activity</div>
            <div className="bg-white border border-[#282828]/8 px-3 py-2 space-y-1">
              <EmailStatusBadge entityId={lead.id} emailType="trial_enquiry_confirmation" label="Enquiry → customer" compact />
              <EmailStatusBadge entityId={lead.id} emailType="trial_enquiry_admin" label="Enquiry → admin" compact />
              {appt && (
                <>
                  <EmailStatusBadge entityId={appt.id} emailType="trial_booking_customer_confirmed" label="Trial confirmed → customer" compact />
                  <EmailStatusBadge entityId={appt.id} emailType="appointment_admin_trial_new" label="Trial booking → admin" compact />
                </>
              )}
            </div>
          </div>

          {/* Linked rentals block — grouped rentals show as one bucket with their children nested */}
          <div className="mb-3">
            <div className="text-[10px] uppercase tracking-wider text-[#676664]/50 mb-1 flex items-center justify-between">
              <span>Linked rentals</span>
              <button type="button" onClick={() => onOpenLinkage(lead)}
                      className="text-[10px] normal-case tracking-normal text-[#9A7B4F] hover:underline"
                      data-testid={`add-rental-${lead.id}`}>+ Add rental</button>
            </div>
            {rentalCount === 0 ? (
              <div className="text-[12px] text-[#676664]/50 italic">No rentals linked yet.</div>
            ) : (
              <div className="space-y-2" data-testid={`linked-rentals-list-${lead.id}`}>
                {linkedRentalsGrouped.groups.map(g => (
                  <div key={`g-${g.gid}`} className="bg-white border border-[#9A7B4F]/30" data-testid={`linked-group-${g.gid}`}>
                    <div className="flex items-center gap-2 px-3 py-2 bg-[#9A7B4F]/8">
                      <LinkIcon className="w-3 h-3 text-[#9A7B4F]" />
                      <span className="text-[11px] text-[#9A7B4F] font-medium">Group {g.gid}</span>
                      <span className="text-[10px] text-[#676664]/60">
                        · {g.children.length} {g.children.length === 1 ? "child booking" : "child bookings"}
                      </span>
                      <button type="button" onClick={() => onUnlinkGroup(lead, g.gid)}
                              className="ml-auto text-[10px] text-[#676664]/50 hover:text-red-500 uppercase tracking-wider"
                              data-testid={`unlink-group-${g.gid}`}>Unlink</button>
                    </div>
                    {g.children.length > 0 && (
                      <div className="divide-y divide-[#282828]/8">
                        {g.children.map(c => (
                          <div key={c.id} className="px-4 py-2 flex items-center gap-2 text-[11px] text-[#676664]"
                               data-testid={`linked-group-child-${c.id}`}>
                            <ChevronRight className="w-3 h-3 text-[#676664]/40" />
                            <span className="text-[#282828]">{c.outfit_name || "—"}</span>
                            {c.rental_option_name && <span className="text-[#676664]/60">· {c.rental_option_name}</span>}
                            <span className="text-[10px] text-[#676664]/50 ml-auto">{c.id}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
                {linkedRentalsGrouped.singles.map(b => (
                  <div key={`b-${b.id}`} className="bg-white border border-[#9A7B4F]/30 px-3 py-2 text-[11px] flex items-center gap-2"
                       data-testid={`linked-booking-${b.id}`}>
                    <LinkIcon className="w-3 h-3 text-[#9A7B4F]" />
                    <span className="text-[#282828]">{b.outfit_name || b.id}</span>
                    <span className="text-[10px] text-[#676664]/60">· {b.id}</span>
                    <button type="button" onClick={() => onUnlinkBooking(lead, b.id)}
                            className="ml-auto text-[10px] text-[#676664]/50 hover:text-red-500 uppercase tracking-wider"
                            data-testid={`unlink-booking-${b.id}`}>Unlink</button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Linkage audit log */}
          {Array.isArray(lead.linkage_audit) && lead.linkage_audit.length > 0 && (
            <div className="mb-3" data-testid={`linkage-audit-${lead.id}`}>
              <div className="text-[10px] uppercase tracking-wider text-[#676664]/50 mb-1">Linkage audit</div>
              <ul className="text-[10px] text-[#676664] space-y-0.5 bg-white border border-[#282828]/8 px-3 py-2 max-h-32 overflow-y-auto">
                {lead.linkage_audit.slice(-8).reverse().map((e, i) => (
                  <li key={i} className="flex flex-wrap items-baseline gap-1">
                    <span className="text-[9px] uppercase tracking-wider text-[#9A7B4F]">{e.action}</span>
                    {e.booking_id && <span>{e.booking_id}</span>}
                    {e.group_id && <span>{e.group_id}</span>}
                    <span className="text-[9px] text-[#676664]/60 ml-auto">
                      {e.admin_email} · {new Date(e.at).toLocaleString("en-AU", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Row actions */}
          <div className="flex flex-wrap items-center gap-2 pt-3 border-t border-[#282828]/8">
            <button type="button" onClick={() => onOpenLinkage(lead)}
                    className="text-[10px] text-[#9A7B4F] border border-[#9A7B4F]/30 hover:bg-[#9A7B4F]/5 px-3 py-1.5 tracking-wider uppercase inline-flex items-center gap-1"
                    data-testid={`action-link-rental-${lead.id}`}>
              <LinkIcon className="w-3 h-3" /> Link rental
            </button>
            <button type="button" onClick={() => onOpenOutcome(lead)}
                    className="text-[10px] text-[#9A7B4F] border border-[#9A7B4F]/30 hover:bg-[#9A7B4F]/5 px-3 py-1.5 tracking-wider uppercase inline-flex items-center gap-1"
                    data-testid={`action-update-outcome-${lead.id}`}>
              <CheckCircle2 className="w-3 h-3" /> {lead.outcome ? "Update outcome" : "Mark outcome"}
            </button>
            {lead.phone && (
              <button type="button" onClick={() => onSendSms(lead)}
                      className="text-[10px] text-[#9A7B4F] border border-[#9A7B4F]/30 hover:bg-[#9A7B4F]/5 px-3 py-1.5 tracking-wider uppercase inline-flex items-center gap-1"
                      data-testid={`action-send-sms-${lead.id}`}>
                <MessageSquare className="w-3 h-3" /> Send SMS
              </button>
            )}
            {(lead.status === "new" || lead.status === "contacted") && (
              <button type="button" onClick={() => onCreateBooking(lead)}
                      className="text-[10px] text-white bg-[#9A7B4F] hover:bg-[#8B6B3F] px-3 py-1.5 tracking-wider uppercase inline-flex items-center gap-1"
                      data-testid={`action-create-booking-${lead.id}`}>
                <Plus className="w-3 h-3" /> Create booking
              </button>
            )}
            <div className="ml-auto flex items-center gap-1">
              {lead.status !== "closed" && lead.status !== "booked" && (
                <button type="button" onClick={() => onStatusChange(lead.id, { status: "closed" })}
                        className="text-[10px] text-[#676664]/60 hover:text-red-500 px-2 py-1.5"
                        data-testid={`action-close-lead-${lead.id}`}>
                  Close
                </button>
              )}
              <button type="button" onClick={() => onDelete(lead)}
                      className="text-[10px] text-[#676664]/50 hover:text-red-500 px-2 py-1.5 inline-flex items-center gap-1"
                      data-testid={`action-delete-lead-${lead.id}`}>
                <Trash2 className="w-3 h-3" /> Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────
// Unmatched appointments — appointments that could not be attached to any
// lead. Rendered as its own section so admins can act on them (edit or
// cancel) without dropping them silently.
// ────────────────────────────────────────────────────────────────────────
function UnmatchedAppointmentsSection({ appts, leads, bookingsById, onEditBooking, onCancelBooking, onLinked }) {
  const [linkingAppt, setLinkingAppt] = useState(null);
  if (!appts.length) return null;
  return (
    <div className="mt-8" data-testid="unmatched-appointments-section">
      <h2 className="text-[13px] uppercase tracking-wider text-amber-700 mb-3 flex items-center gap-2">
        <Sparkles className="w-3.5 h-3.5 text-amber-600" strokeWidth={1.5} />
        Unmatched appointments ({appts.length})
        <span className="text-[10px] normal-case tracking-normal text-[#676664]/60 font-normal">
          — trial appointments with no lead attached
        </span>
      </h2>
      <div className="space-y-2">
        {appts.map(appt => (
          <div key={appt.id} className="bg-white border border-amber-200 p-3" data-testid={`unmatched-appt-${appt.id}`}>
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0 flex-1">
                <div className="text-[13px] text-[#282828] font-medium truncate">{appt.customer_name || "—"}</div>
                <div className="text-[11px] text-[#676664]/60 truncate">
                  {appt.customer_email || "no email"}{appt.customer_phone ? ` · ${appt.customer_phone}` : ""} · {appt.id}
                </div>
              </div>
              <div className="hidden sm:flex flex-col items-end text-[11px] text-[#676664] min-w-[140px]">
                <span>{formatDate(appt.date)}</span>
                <span className="text-[10px] text-[#676664]/60">{formatTime(appt.time)} · {appt.duration_minutes || 60}m</span>
              </div>
              <span className={`text-[9px] px-2 py-0.5 tracking-wider uppercase ${APPT_STATUS_STYLES[appt.status] || "bg-[#282828]/5"}`}>
                {appt.status}
              </span>
              <div className="flex items-center gap-1">
                {/* Section 6B Part 1 — link rental from unmatched. */}
                <button type="button" onClick={() => setLinkingAppt(appt)}
                        className="text-[10px] text-[#9A7B4F] border border-[#9A7B4F]/30 hover:bg-[#9A7B4F]/8 px-3 py-1.5 tracking-wider uppercase inline-flex items-center gap-1"
                        data-testid={`unmatched-link-${appt.id}`}>
                  <LinkIcon className="w-3 h-3" /> Link Rental
                </button>
                <button type="button" onClick={() => onEditBooking(appt)} className="p-1.5 text-[#676664]/60 hover:text-[#9A7B4F]"
                        title="Edit" data-testid={`unmatched-edit-${appt.id}`}>
                  <Edit3 className="w-3.5 h-3.5" />
                </button>
                <button type="button" onClick={() => onCancelBooking(appt)} className="p-1.5 text-[#676664]/60 hover:text-red-500"
                        title="Cancel" data-testid={`unmatched-cancel-${appt.id}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
      {linkingAppt && (
        <UnmatchedLinkModal
          appt={linkingAppt}
          leads={leads}
          bookingsById={bookingsById}
          onClose={() => setLinkingAppt(null)}
          onLinked={() => { setLinkingAppt(null); onLinked(); }}
        />
      )}
    </div>
  );
}


// ────────────────────────────────────────────────────────────────────────
// Unmatched → link rental modal
// Shows eligible single + group rentals by exact customer_id / normalised
// email match against the appointment. Never matches by name.
// Two paths: link to an existing lead (same email) OR create a lead from
// the appointment's own details, then link the rental — one atomic call
// to POST /api/booking/admin/appointments/{id}/link-rental.
// ────────────────────────────────────────────────────────────────────────
function UnmatchedLinkModal({ appt, leads, bookingsById, onClose, onLinked }) {
  const [submitting, setSubmitting] = useState(false);
  const [selectedRental, setSelectedRental] = useState(null); // { kind, id, label }
  const [leadChoice, setLeadChoice] = useState("__create__");

  const apptEmail = normEmail(appt.customer_email);
  const apptCustId = appt.customer_id || "";

  // Eligible leads — exact normalised email match. Never by name.
  const matchingLeads = useMemo(() => {
    if (!apptEmail) return [];
    return leads.filter(l => normEmail(l.email) === apptEmail);
  }, [leads, apptEmail]);

  useEffect(() => {
    if (matchingLeads.length > 0) setLeadChoice(matchingLeads[0].id);
    else setLeadChoice("__create__");
  }, [matchingLeads]);

  // Eligible rentals — bookings whose customer_email/customer_id matches the
  // appointment. Grouped bookings collapse into one candidate per group.
  const { candidateGroups, candidateSingles } = useMemo(() => {
    const matched = Object.values(bookingsById || {}).filter(b => {
      const em = normEmail(b?.customer_email);
      const same = (b?.customer_id && apptCustId && b.customer_id === apptCustId)
                   || (em && apptEmail && em === apptEmail);
      return same;
    });
    const groups = {};
    const singles = [];
    matched.forEach(b => {
      const gid = b?.admin_booking_group_id;
      if (gid) {
        if (!groups[gid]) groups[gid] = { gid, children: [], customer_name: b.customer_name };
        groups[gid].children.push(b);
      } else {
        singles.push(b);
      }
    });
    return { candidateGroups: Object.values(groups), candidateSingles: singles };
  }, [bookingsById, apptEmail, apptCustId]);

  const totalCandidates = candidateGroups.length + candidateSingles.length;

  const handleSubmit = async () => {
    if (!selectedRental) { toast.error("Select a rental to link"); return; }
    setSubmitting(true);
    try {
      const payload = {
        create_lead: leadChoice === "__create__",
        lead_id: leadChoice !== "__create__" ? leadChoice : null,
        booking_id: selectedRental.kind === "single" ? selectedRental.id : null,
        group_id:   selectedRental.kind === "group"  ? selectedRental.id : null,
      };
      const res = await axios.post(
        `${API}/booking/admin/appointments/${appt.id}/link-rental`,
        payload,
        { headers: headers() }
      );
      toast.success(res.data.lead_created ? "Lead created and rental linked" : "Rental linked to lead");
      onLinked(res.data);
    } catch (err) {
      const d = err.response?.data?.detail;
      const msg = (d && typeof d === "object" && d.message) ? d.message : (typeof d === "string" ? d : "Link failed");
      toast.error(msg);
    }
    setSubmitting(false);
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
         onClick={onClose} data-testid="unmatched-link-modal">
      <div className="bg-white max-w-xl w-full p-6 border border-[#282828]/8 max-h-[90vh] overflow-y-auto"
           onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-[16px] text-[#282828] font-medium">Link rental to appointment</h3>
          <button onClick={onClose} className="p-1 text-[#676664]/50 hover:text-[#282828]"><X className="w-4 h-4" /></button>
        </div>

        <div className="text-[11px] text-[#676664]/80 mb-4 bg-[#FAF8F0]/60 border border-[#282828]/8 p-3">
          <div className="text-[12px] text-[#282828]">{appt.customer_name || "—"}</div>
          <div>{appt.customer_email || "no email"}{appt.customer_phone ? ` · ${appt.customer_phone}` : ""}</div>
          <div>{formatDate(appt.date)} · {formatTime(appt.time)} · {appt.id}</div>
        </div>

        {/* Lead target */}
        <div className="mb-4">
          <div className="text-[10px] uppercase tracking-wider text-[#676664]/60 mb-1">Lead</div>
          {matchingLeads.length === 0 ? (
            <label className="text-[12px] text-[#282828] flex items-start gap-2 cursor-pointer"
                   data-testid="lead-target-create">
              <input type="radio" checked readOnly className="mt-1" />
              <span>
                Create lead from this appointment
                <span className="block text-[10px] text-[#676664]/60">
                  A new lead will be created using the appointment&apos;s customer details, exactly once.
                </span>
              </span>
            </label>
          ) : (
            <div className="space-y-1.5">
              {matchingLeads.map(l => (
                <label key={l.id} className="text-[12px] text-[#282828] flex items-start gap-2 cursor-pointer"
                       data-testid={`lead-target-existing-${l.id}`}>
                  <input type="radio" name="lead_target" checked={leadChoice === l.id}
                         onChange={() => setLeadChoice(l.id)} className="mt-1" />
                  <span>
                    Attach to existing lead <span className="text-[#9A7B4F]">{l.name}</span>
                    <span className="block text-[10px] text-[#676664]/60">{l.email} · {l.id}</span>
                  </span>
                </label>
              ))}
              <label className="text-[12px] text-[#282828] flex items-start gap-2 cursor-pointer"
                     data-testid="lead-target-create">
                <input type="radio" name="lead_target" checked={leadChoice === "__create__"}
                       onChange={() => setLeadChoice("__create__")} className="mt-1" />
                <span>Create a NEW lead from this appointment
                  <span className="block text-[10px] text-[#676664]/60">
                    Only pick this if none of the matches above is the right customer.
                  </span>
                </span>
              </label>
            </div>
          )}
        </div>

        {/* Rentals */}
        <div className="mb-4">
          <div className="text-[10px] uppercase tracking-wider text-[#676664]/60 mb-1">
            Eligible rentals ({totalCandidates})
          </div>
          {totalCandidates === 0 ? (
            <div className="text-[12px] text-[#676664]/60 italic bg-white border border-dashed border-[#282828]/12 p-3"
                 data-testid="link-no-candidates">
              No rentals found for this customer. Rentals must match by exact
              customer id or normalised email. Name-only matches are not accepted.
            </div>
          ) : (
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {candidateGroups.map(g => {
                const active = selectedRental?.kind === "group" && selectedRental.id === g.gid;
                return (
                  <button key={g.gid} type="button"
                          onClick={() => setSelectedRental({ kind: "group", id: g.gid })}
                          className={`w-full text-left border ${active ? "border-[#9A7B4F] bg-[#9A7B4F]/8" : "border-[#282828]/12 hover:bg-[#FAF8F0]/40"} p-3`}
                          data-testid={`candidate-group-${g.gid}`}>
                    <div className="flex items-center gap-2">
                      <span className="text-[9px] uppercase tracking-wider text-[#9A7B4F] bg-[#9A7B4F]/10 px-1.5 py-0.5">Group</span>
                      <span className="text-[12px] text-[#282828]">{g.gid}</span>
                      <span className="text-[10px] text-[#676664]/60">· {g.children.length} {g.children.length === 1 ? "item" : "items"}</span>
                    </div>
                    <div className="mt-1 text-[10px] text-[#676664]/70">
                      {g.children.map(c => c.outfit_name).filter(Boolean).slice(0, 4).join(" · ")}
                    </div>
                  </button>
                );
              })}
              {candidateSingles.map(b => {
                const active = selectedRental?.kind === "single" && selectedRental.id === b.id;
                return (
                  <button key={b.id} type="button"
                          onClick={() => setSelectedRental({ kind: "single", id: b.id })}
                          className={`w-full text-left border ${active ? "border-[#9A7B4F] bg-[#9A7B4F]/8" : "border-[#282828]/12 hover:bg-[#FAF8F0]/40"} p-3`}
                          data-testid={`candidate-single-${b.id}`}>
                    <div className="flex items-center gap-2">
                      <span className="text-[9px] uppercase tracking-wider text-[#676664]/70 bg-[#282828]/5 px-1.5 py-0.5">Single</span>
                      <span className="text-[12px] text-[#282828]">{b.outfit_name || "—"}</span>
                      <span className="text-[10px] text-[#676664]/60">· {b.id}</span>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div className="flex gap-2 justify-end pt-3 border-t border-[#282828]/8">
          <button onClick={onClose} className="text-[11px] text-[#676664] border border-[#282828]/12 px-3 py-2 tracking-wider uppercase">Cancel</button>
          <button onClick={handleSubmit} disabled={submitting || !selectedRental}
                  className="text-[11px] text-white bg-[#9A7B4F] hover:bg-[#8B6B3F] px-4 py-2 tracking-wider uppercase disabled:opacity-40"
                  data-testid="unmatched-link-submit">
            {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Link rental"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────
// Create + Edit modals — retained from previous implementation, minor
// tidy-up only. Behaviour + endpoints unchanged.
// ────────────────────────────────────────────────────────────────────────
function CreateTrialBookingModal({ lead, onClose, onCreated }) {
  const [form, setForm] = useState({
    customer_name: lead?.name || "",
    customer_email: lead?.email || "",
    customer_phone: lead?.phone || "",
    date: lead?.preferred_date || "",
    time: lead?.preferred_time || "",
    duration_minutes: 60,
    notes: "",
    lead_id: lead?.id || "",
  });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.date || !form.time) { toast.error("Date and time are required"); return; }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/booking/admin/trial-bookings`, form, { headers: headers() });
      toast.success("Trial booking created & confirmation sent");
      onCreated(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create booking");
    }
    setSubmitting(false);
  };

  return (
    <div className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="create-trial-modal">
      <div className="bg-white max-w-md w-full p-6 border border-[#282828]/8 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-[17px] text-[#282828]" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 600 }}>
            <Sparkles className="w-4 h-4 inline mr-2 text-[#9A7B4F]" />Create Trial Booking
          </h3>
          <button onClick={onClose} className="p-1 text-[#676664]/30 hover:text-[#282828]"><X className="w-4 h-4" /></button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[#676664]/50 block mb-1">Customer Name</label>
            <input type="text" required value={form.customer_name} onChange={e => setForm(f => ({ ...f, customer_name: e.target.value }))}
              className="w-full border border-[#282828]/12 px-3 py-2.5 text-xs focus:border-[#9A7B4F] focus:outline-none" data-testid="modal-name" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-[#676664]/50 block mb-1">Email</label>
              <input type="email" required value={form.customer_email} onChange={e => setForm(f => ({ ...f, customer_email: e.target.value }))}
                className="w-full border border-[#282828]/12 px-3 py-2.5 text-xs focus:border-[#9A7B4F] focus:outline-none" data-testid="modal-email" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-[#676664]/50 block mb-1">Phone</label>
              <input type="tel" value={form.customer_phone} onChange={e => setForm(f => ({ ...f, customer_phone: e.target.value }))}
                className="w-full border border-[#282828]/12 px-3 py-2.5 text-xs focus:border-[#9A7B4F] focus:outline-none" data-testid="modal-phone" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-[#676664]/50 block mb-1">Date *</label>
              <input type="date" required value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))}
                className="w-full border border-[#282828]/12 px-3 py-2.5 text-xs focus:border-[#9A7B4F] focus:outline-none" data-testid="modal-date" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-[#676664]/50 block mb-1">Time *</label>
              <input type="time" required value={form.time} onChange={e => setForm(f => ({ ...f, time: e.target.value }))}
                className="w-full border border-[#282828]/12 px-3 py-2.5 text-xs focus:border-[#9A7B4F] focus:outline-none" data-testid="modal-time" />
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[#676664]/50 block mb-1">Duration (minutes)</label>
            <input type="number" min={15} step={15} value={form.duration_minutes} onChange={e => setForm(f => ({ ...f, duration_minutes: parseInt(e.target.value) || 60 }))}
              className="w-full border border-[#282828]/12 px-3 py-2.5 text-xs focus:border-[#9A7B4F] focus:outline-none" data-testid="modal-duration" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[#676664]/50 block mb-1">Notes</label>
            <textarea rows={2} value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
              className="w-full border border-[#282828]/12 px-3 py-2.5 text-xs focus:border-[#9A7B4F] focus:outline-none resize-none" data-testid="modal-notes" />
          </div>
          <button type="submit" disabled={submitting}
            className="w-full py-3 bg-[#9A7B4F] hover:bg-[#8B6B3F] text-white text-[11px] tracking-wider uppercase disabled:opacity-50" data-testid="modal-submit">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : "Create & Send Confirmation"}
          </button>
        </form>
      </div>
    </div>
  );
}

function EditTrialBookingModal({ appointment, onClose, onSaved }) {
  const [form, setForm] = useState({
    customer_name: appointment.customer_name || "",
    customer_email: appointment.customer_email || "",
    customer_phone: appointment.customer_phone || "",
    date: appointment.date || "",
    time: appointment.time || "",
    duration_minutes: appointment.duration_minutes || 60,
    notes: appointment.notes || "",
    status: appointment.status || "confirmed",
  });
  const [submitting, setSubmitting] = useState(false);
  const [suggestions, setSuggestions] = useState([]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true); setSuggestions([]);
    try {
      await axios.patch(`${API}/booking/admin/trial-bookings/${appointment.id}`, form, { headers: headers() });
      toast.success("Trial booking updated");
      onSaved();
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail && typeof detail === "object" && Array.isArray(detail.suggestions)) {
        setSuggestions(detail.suggestions);
        toast.error(detail.message || "Time conflict — see suggestions");
      } else {
        toast.error(typeof detail === "string" ? detail : "Update failed");
      }
    }
    setSubmitting(false);
  };

  return (
    <div className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="edit-trial-modal">
      <div className="bg-white max-w-md w-full p-6 border border-[#282828]/8 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-[17px] text-[#282828]" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 600 }}>
            <Edit3 className="w-4 h-4 inline mr-2 text-[#9A7B4F]" />Edit Trial Booking
          </h3>
          <button onClick={onClose} className="p-1 text-[#676664]/30 hover:text-[#282828]"><X className="w-4 h-4" /></button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-[#676664]/50 block mb-1">Date</label>
              <input type="date" required value={form.date} onChange={e => setForm({ ...form, date: e.target.value })}
                className="w-full border border-[#282828]/12 px-3 py-2 text-xs focus:border-[#9A7B4F] focus:outline-none" data-testid="edit-date" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-[#676664]/50 block mb-1">Time</label>
              <input type="time" required value={form.time} onChange={e => setForm({ ...form, time: e.target.value })}
                className="w-full border border-[#282828]/12 px-3 py-2 text-xs focus:border-[#9A7B4F] focus:outline-none" data-testid="edit-time" />
            </div>
          </div>
          {suggestions.length > 0 && (
            <div className="p-3 bg-amber-50 border border-amber-200" data-testid="edit-suggestions">
              <p className="text-[11px] text-amber-800 font-medium mb-2">Try a different time:</p>
              <div className="flex flex-wrap gap-1.5">
                {suggestions.map(t => (
                  <button key={t} type="button" onClick={() => { setForm({ ...form, time: t }); setSuggestions([]); }}
                    className="px-2.5 py-1 text-[11px] bg-white border border-amber-300 hover:bg-amber-100">{t}</button>
                ))}
              </div>
            </div>
          )}
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[#676664]/50 block mb-1">Duration (min)</label>
            <input type="number" min={15} step={15} value={form.duration_minutes} onChange={e => setForm({ ...form, duration_minutes: parseInt(e.target.value) || 60 })}
              className="w-full border border-[#282828]/12 px-3 py-2 text-xs focus:border-[#9A7B4F] focus:outline-none" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[#676664]/50 block mb-1">Customer Name</label>
            <input type="text" value={form.customer_name} onChange={e => setForm({ ...form, customer_name: e.target.value })}
              className="w-full border border-[#282828]/12 px-3 py-2 text-xs focus:border-[#9A7B4F] focus:outline-none" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-[#676664]/50 block mb-1">Email</label>
              <input type="email" value={form.customer_email} onChange={e => setForm({ ...form, customer_email: e.target.value })}
                className="w-full border border-[#282828]/12 px-3 py-2 text-xs focus:border-[#9A7B4F] focus:outline-none" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-[#676664]/50 block mb-1">Phone</label>
              <input type="tel" value={form.customer_phone} onChange={e => setForm({ ...form, customer_phone: e.target.value })}
                className="w-full border border-[#282828]/12 px-3 py-2 text-xs focus:border-[#9A7B4F] focus:outline-none" />
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[#676664]/50 block mb-1">Notes</label>
            <textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows={2}
              className="w-full border border-[#282828]/12 px-3 py-2 text-xs focus:border-[#9A7B4F] focus:outline-none resize-none" />
          </div>
          <div className="flex gap-2 pt-2">
            <button type="button" onClick={onClose} className="flex-1 py-2.5 text-[11px] text-[#676664] border border-[#282828]/12 tracking-wider uppercase">Cancel</button>
            <button type="submit" disabled={submitting}
              className="flex-1 py-2.5 bg-[#9A7B4F] text-white text-[11px] tracking-wider uppercase disabled:opacity-40" data-testid="save-edit-trial-btn">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : "Save Changes"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────
// Admin-notes edit modal (extracted so the compact row can call it).
// ────────────────────────────────────────────────────────────────────────
function AdminNotesModal({ lead, onClose, onSaved }) {
  const [value, setValue] = useState(lead.admin_notes || "");
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      await axios.patch(`${API}/booking/admin/trial-leads/${lead.id}`, { admin_notes: value }, { headers: headers() });
      toast.success("Notes saved");
      onSaved();
    } catch { toast.error("Save failed"); }
    setSaving(false);
  };
  return (
    <div className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="admin-notes-modal">
      <div className="bg-white max-w-md w-full p-6 border border-[#282828]/8" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-[15px] text-[#282828] font-medium">Admin note — {lead.name}</h3>
          <button onClick={onClose} className="p-1 text-[#676664]/40 hover:text-[#282828]"><X className="w-4 h-4" /></button>
        </div>
        <textarea rows={4} value={value} onChange={e => setValue(e.target.value)}
                  placeholder="Internal notes visible to admins only."
                  className="w-full border border-[#282828]/12 px-3 py-2 text-xs focus:border-[#9A7B4F] focus:outline-none resize-none"
                  data-testid="admin-notes-textarea" />
        <div className="flex justify-end gap-2 mt-3">
          <button onClick={onClose} className="text-[11px] text-[#676664] border border-[#282828]/12 px-3 py-2 tracking-wider uppercase">Cancel</button>
          <button onClick={save} disabled={saving}
                  className="text-[11px] text-white bg-[#9A7B4F] hover:bg-[#8B6B3F] px-3 py-2 tracking-wider uppercase disabled:opacity-50"
                  data-testid="admin-notes-save">
            {saving ? "Saving…" : "Save note"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────
// Section 6B Part 2 — Add-rental modal for an ATTACHED lead.
// Uses POST /booking/admin/trial-leads/{id}/link-rental. Filters out any
// rentals that are already linked (single or group). Candidates are matched
// by exact customer_id OR normalised email. Never by name.
// ────────────────────────────────────────────────────────────────────────
function LeadLinkRentalModal({ lead, bookingsById, onClose, onLinked }) {
  const [submitting, setSubmitting] = useState(false);
  const [selectedRental, setSelectedRental] = useState(null);

  const leadEmail = normEmail(lead.email);
  const leadCustId = lead.customer_id || "";
  const existingBids = new Set(lead.linked_booking_ids || []);
  const existingGids = new Set(lead.linked_booking_group_ids || []);
  if (lead.converted_booking_id) existingBids.add(lead.converted_booking_id);

  const { candidateGroups, candidateSingles } = useMemo(() => {
    const matched = Object.values(bookingsById || {}).filter(b => {
      const em = normEmail(b?.customer_email);
      return (b?.customer_id && leadCustId && b.customer_id === leadCustId)
          || (em && leadEmail && em === leadEmail);
    });
    const groups = {};
    const singles = [];
    matched.forEach(b => {
      const gid = b?.admin_booking_group_id;
      if (gid) {
        if (existingGids.has(gid)) return;
        if (!groups[gid]) groups[gid] = { gid, children: [], customer_name: b.customer_name };
        groups[gid].children.push(b);
      } else {
        if (existingBids.has(b.id)) return;
        singles.push(b);
      }
    });
    return { candidateGroups: Object.values(groups), candidateSingles: singles };
  }, [bookingsById, leadEmail, leadCustId, existingBids, existingGids]);

  const totalCandidates = candidateGroups.length + candidateSingles.length;

  const handleSubmit = async () => {
    if (!selectedRental) { toast.error("Select a rental to link"); return; }
    setSubmitting(true);
    try {
      const payload = {
        booking_id: selectedRental.kind === "single" ? selectedRental.id : null,
        group_id:   selectedRental.kind === "group"  ? selectedRental.id : null,
      };
      await axios.post(`${API}/booking/admin/trial-leads/${lead.id}/link-rental`,
        payload, { headers: headers() });
      toast.success("Rental linked");
      onLinked();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error((d && d.message) || (typeof d === "string" ? d : "Link failed"));
    }
    setSubmitting(false);
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
         onClick={onClose} data-testid="lead-link-modal">
      <div className="bg-white max-w-xl w-full p-6 border border-[#282828]/8 max-h-[90vh] overflow-y-auto"
           onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-[16px] text-[#282828] font-medium">Link rental to {lead.name}</h3>
          <button onClick={onClose} className="p-1 text-[#676664]/50 hover:text-[#282828]"><X className="w-4 h-4" /></button>
        </div>
        <div className="text-[11px] text-[#676664]/80 mb-4 bg-[#FAF8F0]/60 border border-[#282828]/8 p-3">
          <div className="text-[12px] text-[#282828]">{lead.name || "—"}</div>
          <div>{lead.email}{lead.phone ? ` · ${lead.phone}` : ""}</div>
        </div>
        <div className="mb-4">
          <div className="text-[10px] uppercase tracking-wider text-[#676664]/60 mb-1">
            Eligible rentals ({totalCandidates})
          </div>
          {totalCandidates === 0 ? (
            <div className="text-[12px] text-[#676664]/60 italic bg-white border border-dashed border-[#282828]/12 p-3">
              No new rentals available for this customer. Rentals must match by
              exact customer id or normalised email; name-only matches are
              rejected. Rentals already linked to this lead are not shown.
            </div>
          ) : (
            <div className="space-y-2 max-h-72 overflow-y-auto">
              {candidateGroups.map(g => {
                const active = selectedRental?.kind === "group" && selectedRental.id === g.gid;
                return (
                  <button key={g.gid} type="button"
                          onClick={() => setSelectedRental({ kind: "group", id: g.gid })}
                          className={`w-full text-left border ${active ? "border-[#9A7B4F] bg-[#9A7B4F]/8" : "border-[#282828]/12 hover:bg-[#FAF8F0]/40"} p-3`}
                          data-testid={`lead-candidate-group-${g.gid}`}>
                    <div className="flex items-center gap-2">
                      <span className="text-[9px] uppercase tracking-wider text-[#9A7B4F] bg-[#9A7B4F]/10 px-1.5 py-0.5">Group</span>
                      <span className="text-[12px] text-[#282828]">{g.gid}</span>
                      <span className="text-[10px] text-[#676664]/60">· {g.children.length} {g.children.length === 1 ? "item" : "items"}</span>
                    </div>
                    <div className="mt-1 text-[10px] text-[#676664]/70">
                      {g.children.map(c => c.outfit_name).filter(Boolean).slice(0, 4).join(" · ")}
                    </div>
                  </button>
                );
              })}
              {candidateSingles.map(b => {
                const active = selectedRental?.kind === "single" && selectedRental.id === b.id;
                return (
                  <button key={b.id} type="button"
                          onClick={() => setSelectedRental({ kind: "single", id: b.id })}
                          className={`w-full text-left border ${active ? "border-[#9A7B4F] bg-[#9A7B4F]/8" : "border-[#282828]/12 hover:bg-[#FAF8F0]/40"} p-3`}
                          data-testid={`lead-candidate-single-${b.id}`}>
                    <div className="flex items-center gap-2">
                      <span className="text-[9px] uppercase tracking-wider text-[#676664]/70 bg-[#282828]/5 px-1.5 py-0.5">Single</span>
                      <span className="text-[12px] text-[#282828]">{b.outfit_name || "—"}</span>
                      <span className="text-[10px] text-[#676664]/60">· {b.id}</span>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
        <div className="flex gap-2 justify-end pt-3 border-t border-[#282828]/8">
          <button onClick={onClose} className="text-[11px] text-[#676664] border border-[#282828]/12 px-3 py-2 tracking-wider uppercase">Cancel</button>
          <button onClick={handleSubmit} disabled={submitting || !selectedRental}
                  className="text-[11px] text-white bg-[#9A7B4F] hover:bg-[#8B6B3F] px-4 py-2 tracking-wider uppercase disabled:opacity-40"
                  data-testid="lead-link-submit">
            {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Link rental"}
          </button>
        </div>
      </div>
    </div>
  );
}


// ════════════════════════════════════════════════════════════════════════
// Page
// ════════════════════════════════════════════════════════════════════════
export default function AdminTrialLeads() {
  const [leads, setLeads] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [bookingsById, setBookingsById] = useState({});
  const [loading, setLoading] = useState(true);

  // UI state — persisted so opening/closing a lead preserves filters/sort.
  const persisted = readUiState();
  const [searchQuery, setSearchQuery] = useState(persisted.searchQuery || "");
  const [statusFilter, setStatusFilter] = useState(persisted.statusFilter || "");
  const [outcomeFilter, setOutcomeFilter] = useState(persisted.outcomeFilter || "");
  const [apptStatusFilter, setApptStatusFilter] = useState(persisted.apptStatusFilter || "");
  const [attendanceFilter, setAttendanceFilter] = useState(persisted.attendanceFilter || "");
  const [linkedFilter, setLinkedFilter] = useState(persisted.linkedFilter || "any"); // any | linked | not_linked
  const [dateFrom, setDateFrom] = useState(persisted.dateFrom || "");
  const [dateTo, setDateTo] = useState(persisted.dateTo || "");
  const [sortMode, setSortMode] = useState(persisted.sortMode || "trial_desc");
  const [expandedLeadId, setExpandedLeadId] = useState(persisted.expandedLeadId || null);
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    writeUiState({ searchQuery, statusFilter, outcomeFilter, apptStatusFilter,
                    attendanceFilter, linkedFilter, dateFrom, dateTo, sortMode, expandedLeadId });
  }, [searchQuery, statusFilter, outcomeFilter, apptStatusFilter, attendanceFilter, linkedFilter,
       dateFrom, dateTo, sortMode, expandedLeadId]);

  // Modal state.
  const [bookingLead, setBookingLead] = useState(null);
  const [editingBooking, setEditingBooking] = useState(null);
  const [convertingLead, setConvertingLead] = useState(null);
  const [linkingLead, setLinkingLead] = useState(null);       // 6B Part 2 add-rental
  const [smsModal, setSmsModal] = useState(null);
  const [outcomeLead, setOutcomeLead] = useState(null);
  const [notesLead, setNotesLead] = useState(null);

  const fetchAll = useCallback(async () => {
    try {
      const [leadsRes, apptsRes, bookingsRes] = await Promise.all([
        axios.get(`${API}/booking/admin/trial-leads?status=`, { headers: headers() }),
        axios.get(`${API}/booking/admin/appointments?booking_type=trial`, { headers: headers() }),
        // Rev 2 — load all bookings once so we can dedupe linked rentals by
        // admin_booking_group_id (Nidhi's group counts as 1 logical rental)
        // and render group children nested underneath.
        axios.get(`${API}/bookings`, { headers: headers() }).catch(() => ({ data: { bookings: [] } })),
      ]);
      setLeads(leadsRes.data.leads || []);
      const all = (apptsRes.data.appointments || []).filter(a => a.status !== "cancelled");
      setBookings(all);
      const list = Array.isArray(bookingsRes.data)
        ? bookingsRes.data
        : (bookingsRes.data?.bookings || []);
      const map = {};
      list.forEach(b => { if (b?.id) map[b.id] = b; });
      setBookingsById(map);
    } catch {
      toast.error("Failed to load trial data");
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // ── Lead ↔ appointment binding (single source of truth) ────────────────
  // Rev 2 — a lead can have MULTIPLE appointments (e.g. reschedules or
  // duplicated confirmations). Priority per appointment attachment:
  //   1. appt.lead_id explicitly set on the appointment.
  //   2. Exact normalised email match to a lead.
  //   3. Exact normalised phone match ONLY when unambiguous
  //      (matches exactly one lead across the current dataset).
  // Every appointment that binds attaches to that lead's list; only
  // appointments that satisfy NONE of these fall into "Unmatched".
  const { appointmentsByLeadId, unmatchedAppointments } = useMemo(() => {
    const byLead = {};
    const attached = new Set();
    const emailCounts = {};
    const phoneCounts = {};
    leads.forEach(l => {
      const em = normEmail(l.email);
      if (em) emailCounts[em] = (emailCounts[em] || 0) + 1;
      const p = String(l.phone || "").replace(/\D+/g, "");
      if (p) phoneCounts[p] = (phoneCounts[p] || 0) + 1;
    });

    const attach = (leadId, appt) => {
      if (!byLead[leadId]) byLead[leadId] = [];
      byLead[leadId].push(appt);
      attached.add(appt.id);
    };

    // Pass 1 — explicit lead_id (strict).
    bookings.forEach(a => {
      if (a.lead_id) {
        const owner = leads.find(l => l.id === a.lead_id);
        if (owner) attach(owner.id, a);
      }
    });
    // Pass 2 — normalised email match.
    bookings.forEach(a => {
      if (attached.has(a.id)) return;
      const em = normEmail(a.customer_email);
      if (!em) return;
      const owners = leads.filter(l => normEmail(l.email) === em);
      if (owners.length > 0) {
        // Prefer the first lead (deterministic by fetch order); every appt
        // with that email attaches to that lead's list.
        attach(owners[0].id, a);
      }
    });
    // Pass 3 — normalised phone, only if unambiguous.
    bookings.forEach(a => {
      if (attached.has(a.id)) return;
      const p = String(a.customer_phone || "").replace(/\D+/g, "");
      if (!p || phoneCounts[p] !== 1) return;
      const owner = leads.find(l => String(l.phone || "").replace(/\D+/g, "") === p);
      if (owner) attach(owner.id, a);
    });

    const unmatched = bookings.filter(a => !attached.has(a.id));
    return { appointmentsByLeadId: byLead, unmatchedAppointments: unmatched };
  }, [leads, bookings]);

  // ── Filters ────────────────────────────────────────────────────────────
  const filteredLeads = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return leads.filter(lead => {
      const apptList = appointmentsByLeadId[lead.id] || [];
      const appt = primaryAppointmentFor(lead, apptList);
      // Trial status pill (lead.status): still respected as coarse filter.
      if (statusFilter && lead.status !== statusFilter) return false;
      if (outcomeFilter && lead.outcome !== outcomeFilter) return false;
      if (apptStatusFilter) {
        const s = appt?.status || (lead.status === "booked" ? "confirmed" : "");
        if (s !== apptStatusFilter) return false;
      }
      if (attendanceFilter) {
        const att = appt?.attendance_status || lead.attendance_status || "awaiting_response";
        if (att !== attendanceFilter) return false;
      }
      if (linkedFilter === "linked" && linkedRentalCount(lead, bookingsById) === 0) return false;
      if (linkedFilter === "not_linked" && linkedRentalCount(lead, bookingsById) > 0) return false;
      // Date range against effective trial date.
      const trialDate = appt?.date || lead.preferred_date;
      if (dateFrom && (!trialDate || trialDate < dateFrom)) return false;
      if (dateTo && (!trialDate || trialDate > dateTo)) return false;
      if (!q) return true;
      const rentalIds = [
        ...(lead.linked_booking_ids || []),
        ...(lead.linked_booking_group_ids || []),
        lead.converted_booking_id || "",
      ];
      // Also include group ids resolved from bookingsById so searching an
      // MBG-* returns the lead that has a child of that group linked.
      (lead.linked_booking_ids || []).forEach(bid => {
        const gid = bookingsById?.[bid]?.admin_booking_group_id;
        if (gid) rentalIds.push(gid);
      });
      const hay = [
        lead.name || "", lead.email || "", lead.phone || "",
        ...apptList.flatMap(a => [a.id, a.customer_email, a.customer_phone]),
        ...rentalIds,
      ].join(" \u0001 ").toLowerCase();
      return hay.includes(q);
    });
  }, [leads, appointmentsByLeadId, bookingsById, searchQuery, statusFilter, outcomeFilter,
       apptStatusFilter, attendanceFilter, linkedFilter, dateFrom, dateTo]);

  // ── Sort ───────────────────────────────────────────────────────────────
  const sortedLeads = useMemo(() => {
    const arr = [...filteredLeads];
    const keyFor = (l) => {
      const apptList = appointmentsByLeadId[l.id] || [];
      return trialDateFor(l, primaryAppointmentFor(l, apptList)).sortKey;
    };
    switch (sortMode) {
      case "trial_asc":
        arr.sort((a, b) => {
          const ka = keyFor(a), kb = keyFor(b);
          if (!ka && !kb) return 0; if (!ka) return 1; if (!kb) return -1;
          return ka.localeCompare(kb);
        });
        break;
      case "updated":
        arr.sort((a, b) => String(b.updated_at || b.created_at || "").localeCompare(String(a.updated_at || a.created_at || "")));
        break;
      case "name":
        arr.sort((a, b) => String(a.name || "").localeCompare(String(b.name || "")));
        break;
      case "trial_desc":
      default:
        arr.sort((a, b) => {
          const ka = keyFor(a), kb = keyFor(b);
          if (!ka && !kb) return 0; if (!ka) return 1; if (!kb) return -1;
          return kb.localeCompare(ka);
        });
    }
    return arr;
  }, [filteredLeads, sortMode, appointmentsByLeadId]);

  // ── Row handlers ───────────────────────────────────────────────────────
  const handleStatusChange = async (leadId, updates) => {
    try {
      await axios.patch(`${API}/booking/admin/trial-leads/${leadId}`, updates, { headers: headers() });
      toast.success("Lead updated");
      fetchAll();
    } catch { toast.error("Update failed"); }
  };
  const handleDeleteLead = async (lead) => {
    if (!window.confirm(`Permanently delete trial lead for ${lead.name}? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/booking/admin/trial-leads/${lead.id}`, { headers: headers() });
      toast.success("Lead deleted");
      fetchAll();
    } catch { toast.error("Delete failed"); }
  };

  // Section 6B Part 2 — Unlink single / group with final-unlink outcome prompt.
  const _unlinkCore = async (lead, payload) => {
    try {
      await axios.post(`${API}/booking/admin/trial-leads/${lead.id}/unlink-rental`,
        payload, { headers: headers() });
      toast.success("Rental unlinked");
      fetchAll();
    } catch (err) {
      const d = err.response?.data?.detail;
      if (d && d.code === "final_unlink_prompt_required") {
        const opts = (d.options || ["keep", "follow_up", "did_not_convert"]).join(" / ");
        const ans = window.prompt(
          `${d.message}\n\nType one of: ${opts}`, "keep");
        if (!ans) return;
        const norm = String(ans).trim().toLowerCase();
        if (!["keep", "follow_up", "did_not_convert"].includes(norm)) {
          toast.error("Invalid choice — unlink cancelled");
          return;
        }
        return _unlinkCore(lead, { ...payload, new_outcome: norm });
      }
      toast.error((d && d.message) || (typeof d === "string" ? d : "Unlink failed"));
    }
  };
  const handleUnlinkBooking = (lead, bookingId) => {
    if (!window.confirm(`Unlink ${bookingId} from this trial?`)) return;
    return _unlinkCore(lead, { booking_id: bookingId });
  };
  const handleUnlinkGroup = (lead, groupId) => {
    if (!window.confirm(`Unlink group ${groupId} from this trial?`)) return;
    return _unlinkCore(lead, { group_id: groupId });
  };
  const handleCancelBooking = async (appt) => {
    if (!window.confirm(`Cancel trial booking on ${appt.date} at ${appt.time}?`)) return;
    try {
      await axios.delete(`${API}/booking/admin/trial-bookings/${appt.id}`, { headers: headers() });
      toast.success("Trial booking cancelled");
      fetchAll();
    } catch { toast.error("Cancel failed"); }
  };
  const toggleExpand = (id) => setExpandedLeadId(cur => (cur === id ? null : id));

  const newCount = leads.filter(l => l.status === "new").length;

  if (loading) return (
    <AdminLayout title="Trial Leads" subtitle="Manage trial enquiries and bookings">
      <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-[#9A7B4F]" /></div>
    </AdminLayout>
  );

  return (
    <AdminLayout
      title="Trial Leads"
      subtitle="Single operational view for enquiries, appointments, outcomes and rental linkage"
      actions={
        <Button onClick={() => setBookingLead({ name: "", email: "", phone: "" })}
                className="bg-[#9A7B4F] hover:bg-[#8B6B3F] text-white px-5"
                data-testid="new-trial-booking-btn">
          <Plus className="w-4 h-4 mr-2" /> New Booking
        </Button>
      }
    >
      {/* Search + sort + filter toggle */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="flex items-center gap-2 flex-1 min-w-[220px] max-w-md">
          <div className="relative w-full">
            <Search className="w-3.5 h-3.5 text-[#676664]/40 absolute left-3 top-1/2 -translate-y-1/2" />
            <input type="search"
                   value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
                   placeholder="Search name, email, phone, appointment ID, booking ID or MBG-*"
                   className="w-full border border-[#282828]/12 pl-9 pr-3 py-2 text-xs focus:border-[#9A7B4F] focus:outline-none"
                   data-testid="trial-leads-search-input" />
          </div>
        </div>
        <div className="flex items-center gap-2">
          <ArrowUpDown className="w-3.5 h-3.5 text-[#676664]/50" />
          <select value={sortMode} onChange={e => setSortMode(e.target.value)}
                  className="border border-[#282828]/12 px-2 py-2 text-xs focus:border-[#9A7B4F] focus:outline-none"
                  data-testid="trial-leads-sort">
            <option value="trial_desc">Trial date — latest to oldest</option>
            <option value="trial_asc">Trial date — oldest to latest</option>
            <option value="updated">Recently updated</option>
            <option value="name">Customer name A–Z</option>
          </select>
          <button type="button" onClick={() => setShowFilters(v => !v)}
                  className={`inline-flex items-center gap-1 border px-3 py-2 text-xs tracking-wider uppercase ${showFilters ? "border-[#9A7B4F] text-[#9A7B4F] bg-[#9A7B4F]/8" : "border-[#282828]/12 text-[#676664] hover:text-[#282828]"}`}
                  data-testid="trial-leads-filter-toggle">
            <Filter className="w-3 h-3" /> Filters
          </button>
        </div>
      </div>

      {/* Expanded filter row */}
      {showFilters && (
        <div className="mb-4 grid sm:grid-cols-2 lg:grid-cols-5 gap-2 p-3 bg-[#FAF8F0]/50 border border-[#282828]/8" data-testid="trial-leads-filter-panel">
          <label className="text-[10px] text-[#676664]/70">
            Trial status
            <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
                    className="w-full border border-[#282828]/12 px-2 py-1.5 text-xs mt-1"
                    data-testid="filter-trial-status">
              <option value="">Any</option>
              <option value="new">New</option>
              <option value="contacted">Contacted</option>
              <option value="booked">Booked</option>
              <option value="closed">Closed</option>
            </select>
          </label>
          <label className="text-[10px] text-[#676664]/70">
            Trial outcome
            <select value={outcomeFilter} onChange={e => setOutcomeFilter(e.target.value)}
                    className="w-full border border-[#282828]/12 px-2 py-1.5 text-xs mt-1"
                    data-testid="filter-trial-outcome">
              <option value="">Any</option>
              <option value="converted">Converted</option>
              <option value="follow_up">Follow up</option>
              <option value="did_not_convert">Did not convert</option>
              <option value="no_show">No show</option>
              <option value="customer_cancelled">Customer cancelled</option>
              <option value="admin_cancelled">Admin cancelled</option>
              <option value="rescheduled">Rescheduled</option>
              <option value="could_not_contact">Could not contact</option>
            </select>
          </label>
          <label className="text-[10px] text-[#676664]/70">
            Appointment status
            <select value={apptStatusFilter} onChange={e => setApptStatusFilter(e.target.value)}
                    className="w-full border border-[#282828]/12 px-2 py-1.5 text-xs mt-1"
                    data-testid="filter-appt-status">
              <option value="">Any</option>
              <option value="confirmed">Confirmed</option>
              <option value="rescheduled">Rescheduled</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
              <option value="no_show">No show</option>
            </select>
          </label>
          <label className="text-[10px] text-[#676664]/70">
            Attendance
            <select value={attendanceFilter} onChange={e => setAttendanceFilter(e.target.value)}
                    className="w-full border border-[#282828]/12 px-2 py-1.5 text-xs mt-1"
                    data-testid="filter-attendance">
              <option value="">Any</option>
              <option value="awaiting_response">Awaiting response</option>
              <option value="confirmed_attending">Confirmed attending</option>
              <option value="not_attending">Not attending</option>
              <option value="no_response">No response</option>
              <option value="cancelled">Cancelled</option>
              <option value="completed">Completed</option>
            </select>
          </label>
          <label className="text-[10px] text-[#676664]/70">
            Linked rentals
            <select value={linkedFilter} onChange={e => setLinkedFilter(e.target.value)}
                    className="w-full border border-[#282828]/12 px-2 py-1.5 text-xs mt-1"
                    data-testid="filter-linked">
              <option value="any">Any</option>
              <option value="linked">Linked</option>
              <option value="not_linked">Not linked</option>
            </select>
          </label>
          <div className="flex gap-1 items-end">
            <label className="text-[10px] text-[#676664]/70 flex-1">
              From
              <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
                     className="w-full border border-[#282828]/12 px-2 py-1.5 text-xs mt-1"
                     data-testid="filter-date-from" />
            </label>
            <label className="text-[10px] text-[#676664]/70 flex-1">
              To
              <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
                     className="w-full border border-[#282828]/12 px-2 py-1.5 text-xs mt-1"
                     data-testid="filter-date-to" />
            </label>
          </div>
          <div className="sm:col-span-2 lg:col-span-5 flex justify-end">
            <button type="button" onClick={() => {
              setSearchQuery(""); setStatusFilter(""); setOutcomeFilter("");
              setApptStatusFilter(""); setAttendanceFilter(""); setLinkedFilter("any");
              setDateFrom(""); setDateTo(""); setSortMode("trial_desc");
            }} className="text-[10px] text-[#676664] underline"
              data-testid="filter-reset">Reset all</button>
          </div>
        </div>
      )}

      {/* Status pills — kept for continuity with previous UX */}
      <div className="flex gap-1 mb-4 border-b border-[#282828]/8 overflow-x-auto">
        {[
          { key: "", label: `All (${leads.length})` },
          { key: "new", label: `New${newCount > 0 ? ` (${newCount})` : ""}` },
          { key: "contacted", label: "Contacted" },
          { key: "booked", label: "Booked" },
          { key: "closed", label: "Closed" },
        ].map(f => (
          <button key={f.key} onClick={() => setStatusFilter(f.key)}
            className={`px-5 py-2.5 text-[11px] tracking-wider transition-colors border-b-2 -mb-[1px] flex-shrink-0 ${statusFilter === f.key ? "border-[#9A7B4F] text-[#9A7B4F] font-medium" : "border-transparent text-[#676664]/50 hover:text-[#676664]"}`}
            data-testid={`filter-${f.key || "all"}`}>
            {f.label}
          </button>
        ))}
      </div>

      {/* Leads list */}
      {sortedLeads.length === 0 ? (
        <div className="text-center py-12 bg-white border border-[#282828]/8">
          <Sparkles className="w-8 h-8 text-[#676664]/15 mx-auto mb-3" strokeWidth={1} />
          <p className="text-[13px] text-[#676664]/40">
            {leads.length === 0 ? "No trial leads yet" : "No leads match the current filters."}
          </p>
        </div>
      ) : (
        <div className="space-y-2" data-testid="trial-leads-list">
          {sortedLeads.map(lead => (
            <LeadRow
              key={lead.id}
              lead={lead}
              appts={appointmentsByLeadId[lead.id] || []}
              bookingsById={bookingsById}
              expanded={expandedLeadId === lead.id}
              onToggle={toggleExpand}
              onOpenLinkage={setLinkingLead}
              onOpenOutcome={setOutcomeLead}
              onSendSms={(l) => setSmsModal({ to_phone: resolveCustomerPhone(l, appointmentsByLeadId[l.id]) || l.phone, customer_name: l.name, trial_lead_id: l.id })}
              onCreateBooking={setBookingLead}
              onEditBooking={setEditingBooking}
              onDelete={handleDeleteLead}
              onStatusChange={handleStatusChange}
              onEditNotes={setNotesLead}
              onUnlinkBooking={handleUnlinkBooking}
              onUnlinkGroup={handleUnlinkGroup}
            />
          ))}
        </div>
      )}

      {/* Unmatched appointments exception view */}
      <UnmatchedAppointmentsSection
        appts={unmatchedAppointments}
        leads={leads}
        bookingsById={bookingsById}
        onEditBooking={setEditingBooking}
        onCancelBooking={handleCancelBooking}
        onLinked={fetchAll}
      />

      {/* Modals */}
      {bookingLead && (
        <CreateTrialBookingModal lead={bookingLead}
          onClose={() => setBookingLead(null)}
          onCreated={() => { setBookingLead(null); fetchAll(); }} />
      )}
      {editingBooking && (
        <EditTrialBookingModal appointment={editingBooking}
          onClose={() => setEditingBooking(null)}
          onSaved={() => { setEditingBooking(null); fetchAll(); }} />
      )}
      {convertingLead && (
        <TrialConversionModal lead={convertingLead}
          onClose={() => setConvertingLead(null)}
          onConverted={() => { setConvertingLead(null); fetchAll(); }} />
      )}
      {linkingLead && (
        <LeadLinkRentalModal lead={linkingLead} bookingsById={bookingsById}
          onClose={() => setLinkingLead(null)}
          onLinked={() => { setLinkingLead(null); fetchAll(); }} />
      )}
      {smsModal && (
        <SmsReplyModal to={smsModal} title="Send SMS to trial lead"
          template_key="admin_direct_trial_sms"
          onClose={() => setSmsModal(null)}
          onSent={() => setSmsModal(null)} />
      )}
      {outcomeLead && (
        <CloseTrialModal entity={outcomeLead} entityKind="trial_lead"
          onClose={() => setOutcomeLead(null)}
          onClosed={async () => { await fetchAll(); }} />
      )}
      {notesLead && (
        <AdminNotesModal lead={notesLead}
          onClose={() => setNotesLead(null)}
          onSaved={() => { setNotesLead(null); fetchAll(); }} />
      )}
    </AdminLayout>
  );
}
